import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronRight, ChevronLeft, Trophy, Users, DollarSign, Target, Sparkles } from "lucide-react";

interface OnboardingStep {
  icon: React.ReactNode;
  emoji: string;
  title: string;
  description: string;
  highlight?: string;
}

const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    icon: <Trophy className="w-12 h-12" />,
    emoji: "🏌️",
    title: "Welcome to SettleUp",
    description: "The ultimate golf competition tracker for your group. Track skins, side bets, and settle up with friends—all in one place.",
    highlight: "No more scribbling on scorecards!",
  },
  {
    icon: <Users className="w-12 h-12" />,
    emoji: "👥",
    title: "Play Together",
    description: "Create a round and share the room code with your golf buddies. Everyone sees live score updates on their own phone.",
    highlight: "Real-time sync across devices",
  },
  {
    icon: <DollarSign className="w-12 h-12" />,
    emoji: "💰",
    title: "Track Skins & Side Bets",
    description: "Set stakes for skins games, greenies on par 3s, and sandies. The app calculates who owes who automatically.",
    highlight: "No more math headaches!",
  },
  {
    icon: <Target className="w-12 h-12" />,
    emoji: "📊",
    title: "Build Your Stats",
    description: "Create an account to save your round history. Track lifetime winnings, skins won, and compete on the leaderboard.",
    highlight: "See your golf money story unfold",
  },
];

interface OnboardingScreenProps {
  onComplete: () => void;
}

export const OnboardingScreen = ({ onComplete }: OnboardingScreenProps) => {
  const [currentStep, setCurrentStep] = useState(0);

  const step = ONBOARDING_STEPS[currentStep];
  const isLastStep = currentStep === ONBOARDING_STEPS.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      onComplete();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/20 via-background to-background flex flex-col">
      {/* Header with skip */}
      <header className="px-4 py-4 flex justify-end">
        <Button variant="ghost" size="sm" onClick={handleSkip}>
          Skip
        </Button>
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-8">
        {/* Animated emoji */}
        <div className="text-7xl mb-6 animate-bounce">{step.emoji}</div>

        {/* Step content */}
        <Card className="w-full max-w-sm bg-card/80 backdrop-blur border-primary/20">
          <CardContent className="p-6 text-center">
            <div className="flex justify-center mb-4 text-primary">
              {step.icon}
            </div>
            <h1 className="text-2xl font-bold mb-3">{step.title}</h1>
            <p className="text-muted-foreground mb-4">{step.description}</p>
            {step.highlight && (
              <div className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary/10 rounded-full text-sm font-medium text-primary">
                <Sparkles className="w-4 h-4" />
                {step.highlight}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Progress dots */}
        <div className="flex gap-3 mt-8">
          {ONBOARDING_STEPS.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentStep(index)}
              aria-label={`Go to step ${index + 1}`}
              className="min-w-[24px] min-h-[24px] p-1.5 flex items-center justify-center"
            >
              <span
                className={`block w-2.5 h-2.5 rounded-full transition-all ${
                  index === currentStep
                    ? "bg-primary w-8"
                    : index < currentStep
                    ? "bg-primary/50"
                    : "bg-muted"
                }`}
              />
            </button>
          ))}
        </div>
      </main>

      {/* Navigation buttons */}
      <footer className="px-6 py-6 flex gap-4">
        <Button
          variant="outline"
          size="lg"
          onClick={handlePrev}
          disabled={currentStep === 0}
          className="flex-1"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back
        </Button>
        <Button
          size="lg"
          onClick={handleNext}
          className="flex-1"
        >
          {isLastStep ? "Get Started" : "Next"}
          {!isLastStep && <ChevronRight className="w-5 h-5 ml-1" />}
        </Button>
      </footer>
    </div>
  );
};